// Vue & Plugins
import { MaybeRefOrGetter, toValue } from 'vue'

// Composables
import { useQuery, keepPreviousData, useInfiniteQuery, useMutation } from '@tanstack/vue-query'

// Models
import { Site, type SiteID } from '../../sites/models/site'
import { Area, AreaID } from '../models/area'

// Services
import { AreaCreateForm, AreaSearchForm, AreaUpdateForm, AreaSkillSyncForm, create, fetch, fetchByBuilding, show, update, archive, restore, syncSkills } from '../areaService'
import { BuildingID } from '../../buildings/models/building'
import { useEventStore } from '../../common/eventStore'
import { ToastEvent } from '../../common/models/toastEvent'

interface AreaFilters {
    name?: MaybeRefOrGetter<string>
    archived?: MaybeRefOrGetter<boolean>

    buildingId?: MaybeRefOrGetter<BuildingID | null | undefined>

    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>
}

export const areaKeys = {
    all: ['areas'] as const,
    lists: () => [...areaKeys.all, 'list'] as const,
    list: (siteId: MaybeRefOrGetter<SiteID>, filters: AreaFilters) => [
        ...areaKeys.lists(),
        siteId,
        {
            name: filters.name,
            archived: filters.archived,
            buildingId: filters.buildingId,
            perPage: filters.perPage, page: filters.page,
        },
    ] as const,
    details: () => [...areaKeys.all, 'detail'] as const,
    detail: (siteId: MaybeRefOrGetter<SiteID>, id: MaybeRefOrGetter) => [
        ...areaKeys.details(),
        siteId,
        id,
    ] as const,
    listByDepartment: (departmentId: MaybeRefOrGetter<BuildingID>, filters: AreaFilters) => [
        ...areaKeys.lists(),
        departmentId,
        {
            name: filters.name,
            archived: filters.archived,
            perPage: filters.perPage,
            page: filters.page,
        },
    ] as const,
}

const filtersToForm = (filters: AreaFilters): AreaSearchForm => {
    return {
        name: toValue(filters.name),
        archived: toValue(filters.archived),
        perPage: toValue(filters.perPage ?? 15),
        page: toValue(filters.page ?? 1),
    }
}

export function useAreaQuery(siteId: MaybeRefOrGetter<SiteID>, filters: AreaFilters = {}, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    return useQuery({
        queryKey: areaKeys.list(siteId, filters),
        queryFn: async () => {
            const form = filtersToForm(filters)

            const buildingId = toValue(filters.buildingId)
            return await (buildingId ? fetchByBuilding(toValue(siteId), buildingId, form) : fetch(toValue(siteId), form))
        },
        placeholderData: keepPreviousData,
        enabled: options.enabled,
    })
}

export function useAreaQueryInfinite(siteId: MaybeRefOrGetter<SiteID>, filters: AreaFilters = {}, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    return useInfiniteQuery({
        queryKey: areaKeys.list(siteId, filters),
        queryFn: async ({ pageParam }) => {
            const form = filtersToForm({...filters, perPage: 15, page: pageParam})

            const buildingId = toValue(filters.buildingId)
            return await (buildingId ? fetchByBuilding(toValue(siteId), buildingId, form) : fetch(toValue(siteId), form))
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.pagination.currentPage + 1

            if (nextPage > lastPage.pagination.totalPages) {
                return undefined
            }

            return nextPage
        },
        getPreviousPageParam: (firstPage) => {
            const previousPage = firstPage.pagination.currentPage - 1

            if (previousPage < 1) {
                return undefined
            }

            return previousPage
        },
        enabled: options.enabled,
    })
}

export function useAreaDetailQuery(siteId: MaybeRefOrGetter<SiteID>, areaId: MaybeRefOrGetter<AreaID>, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    return useQuery({
        queryKey: areaKeys.detail(siteId, areaId),
        queryFn: async () => {
            return await show(toValue(siteId), toValue(areaId))
        },
        enabled: options.enabled,
    })
}

export function useAreaCreateMutation(site: MaybeRefOrGetter<Site>, departmentId: MaybeRefOrGetter<BuildingID>) {
    const eventStore = useEventStore()

    return useMutation<{ area: Area }, Error, { form: AreaCreateForm }>({
        mutationFn: async (areaCreate) => {
            return create(toValue(site), toValue(departmentId), areaCreate.form)
        },
        onSuccess: async () => {
            const event = new ToastEvent('Saved')
            eventStore.addEvent(event)
        },
    })
}

export function useAreaUpdateMutation(siteId: MaybeRefOrGetter<SiteID>, areaId: MaybeRefOrGetter<AreaID>) {
    return useMutation<{ area: Area }, Error, { form: AreaUpdateForm }>({
        mutationFn: async (areaUpdate) => {
            return update(toValue(siteId), toValue(areaId), areaUpdate.form)
        },
    })
}

export function useAreaArchiveMutation(site: MaybeRefOrGetter<Site>, areaId: MaybeRefOrGetter<AreaID>) {
    return useMutation({
        mutationFn: async () => {
            return archive(toValue(site), toValue(areaId))
        },
    })
}

export function useAreaRestoreMutation(site: MaybeRefOrGetter<Site>, areaId: MaybeRefOrGetter<AreaID>) {
    return useMutation({
        mutationFn: async () => {
            return restore(toValue(site), toValue(areaId))
        },
    })
}

export function useAreaSkillSyncMutation(siteId: MaybeRefOrGetter<SiteID>, areaId: MaybeRefOrGetter<AreaID>) {
    return useMutation<{ area: Area }, Error, { form: AreaSkillSyncForm }>({
        mutationFn: async (areaUpdate) => {
            return syncSkills(toValue(siteId), toValue(areaId), areaUpdate.form)
        },
    })
}